import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import useIntercomCallStore from '../stores/intercomCallStore';

/**
 * Shared intercom control logic (Unlock + Talk) used by both the Intercoms list
 * cards and the IntercomView page. Extracted verbatim from Intercoms.jsx so the
 * two screens share one implementation instead of duplicating it. Each consumer
 * gets its own unlocking/unlockSuccess state (only one action runs at a time on
 * a given screen).
 */
export default function useIntercomControls() {
  const navigate = useNavigate();
  const [unlocking, setUnlocking] = useState(false);
  const [unlockSuccess, setUnlockSuccess] = useState(false);
  const { callState, answerCall, endCall, activeIntercomId } = useIntercomCallStore();

  const handleUnlock = async (intercomId) => {
    setUnlocking(true);
    try {
      const response = await fetchWithTenant(`/api/intercom/${intercomId}/unlock`, {
        method: 'POST',
      });
      const result = await response.json();

      if (result.success) {
        setUnlockSuccess(true);
        toast.success('Door unlocked successfully');
        setTimeout(() => setUnlockSuccess(false), 3000);
      } else {
        toast.error(result.message || 'Failed to unlock door');
      }
    } catch (error) {
      toast.error('Failed to unlock door');
    } finally {
      setUnlocking(false);
    }
  };

  const handleTalk = async (intercom) => {
    if (callState === 'active' && activeIntercomId === intercom.id) {
      endCall();
      navigate('/intercoms');
      return;
    }
    const tenantId = localStorage.getItem('currentTenantId') || 'default';
    const success = await answerCall(
      intercom.id,
      intercom.cameraId,
      intercom.name,
      tenantId
    );
    if (!success) {
      toast.error('Failed to start audio - check microphone permissions');
    }
  };

  return { unlocking, unlockSuccess, handleUnlock, handleTalk, callState, activeIntercomId };
}
